import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  CardHeader,
  Button,
  Input,
} from "reactstrap";
import BannerTable from "../PageComponents/BannerTable";
import BannerCard from "../PageComponents/BannerCard";
import BannerFormModal from "../PageComponents/BannerFormModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import DeactivateModal from "./DeactivateModal";
import { Grid, List } from "react-feather";
import "./Banner.scss";
import {
  createBanner,
  updateBanner,
  deleteBanner,
  getBannersByFilter,
  deactivateBanner
} from "../../../../../api-handle/bannerApi";
import { toast } from "react-toastify";

const BannerManagement = () => {
  const [viewType, setViewType] = useState("table");
  const [filterStatus, setFilterStatus] = useState("");

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [targetBannerId, setTargetBannerId] = useState(null);

  const fetchBanners = async (page = 1, size = perPage) => {
    setLoading(true);
    try {
      const queryParams = `?status=${filterStatus}&page=${page}&size=${size}`;
      const response = await getBannersByFilter(queryParams);
      setBanners(response?.filteredData?.banners || []);
      setTotalRows(response?.filteredData?.pagination?.totalItems || 0);
    } catch (err) {
      console.error("Error fetching banners:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners(currentPage, perPage);
  }, [filterStatus, currentPage, perPage]);

  const handleSaveBanner = async (banner) => {
    if (banner._id) {
      await updateBanner(banner);
    } else {
      await createBanner(banner);
    }
    setIsModalOpen(false);
    fetchBanners(currentPage, perPage); // Refresh data
  };

  const handleDelete = (id) => {
    setTargetBannerId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    await deleteBanner(targetBannerId);
    setDeleteModalOpen(false);
    setTargetBannerId(null);
    fetchBanners(currentPage, perPage); // Refresh data
  };

  const handleDeactivate = async (id) => {
    setTargetBannerId(id);
    const banner = banners.find((b) => b._id === id);
    if (banner.status === "pending" || banner.status === "declined") {
      toast.warn("Banner is pending approval. You can activate or deactivate it once approved.");
      return
    }
    await deactivateBanner(id);
    setBanners((prevBanners) =>
      prevBanners.map((banner) =>
        banner._id === id
          ? { ...banner, status: banner.status === "approved" ? "deactivated" : "approved" }
          : banner
      )
    );
  };

  const handleConfirmDeactivate = async () => {
    await deactivateBanner(targetBannerId);
    setDeactivateModalOpen(false);
    setTargetBannerId(null);
    fetchBanners(currentPage, perPage); // Refresh data
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const toggleView = () => {
    setViewType(viewType === "table" ? "cards" : "table");
  };

  return (
    <Container fluid>
      <Row>
        <Col xl="12">
          <CardHeader className="banner-header-container">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 w-100">

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Input
                  type="select"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{ minWidth: "150px" }}
                >
                  <option value="">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="declined">Declined</option>
                  {/* <option value="deactivated">Deactivated</option> */}
                </Input>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button color="light" onClick={toggleView}>
                  {viewType === "table" ? <Grid size={18} /> : <List size={18} />}
                </Button>

                <Button
                  color="primary"
                  onClick={() => {
                    setSelectedBanner(null);
                    setIsModalOpen(true);
                  }}
                >
                  Add New Banner
                </Button>
              </div>

            </div>
          </CardHeader>

          {viewType === "table" ? (
            <BannerTable
              banners={banners}
              loading={loading}
              onEdit={(data) => {
                setSelectedBanner(data);
                setIsModalOpen(true);
              }}
              onDelete={handleDelete}
              onDeActivate={handleDeactivate}
              totalRows={totalRows}
              perPage={perPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handlePerRowsChange}
            />
          ) : (
            <BannerCard
              data={banners}
              onEdit={(data) => {
                setSelectedBanner(data);
                setIsModalOpen(true);
              }}
              onDelete={handleDelete}
              onToggleStatus={handleDeactivate}
              totalRows={totalRows}
              perPage={perPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          )}
        </Col>
      </Row>

      <BannerFormModal
        isOpen={isModalOpen}
        toggle={() => setIsModalOpen(!isModalOpen)}
        bannerData={selectedBanner}
        onSave={handleSaveBanner}
      />

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(!deleteModalOpen)}
        onConfirm={handleConfirmDelete}
      />

      <DeactivateModal
        isOpen={deactivateModalOpen}
        toggle={() => setDeactivateModalOpen(!deactivateModalOpen)}
        onConfirm={handleConfirmDeactivate}
      />
    </Container>
  );
};

export default BannerManagement;